import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { dkk, num, dec } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, Tooltip, XAxis,
} from "recharts";
import {
  Store, LogOut, RefreshCw, CalendarDays, ArrowUpRight, ArrowDownRight,
  Target, Check, Pencil, X, Monitor, Settings2, ChevronDown,
  BarChart3, Wallet, Boxes, Megaphone, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { ProductStats } from "@/components/ProductStats";
import { CategoryStats } from "@/components/CategoryStats";
import { FinanceStats } from "@/components/FinanceStats";
import { PaymentMethods } from "@/components/PaymentMethods";
import { HourlyStats } from "@/components/HourlyStats";
import { HolidayOverview } from "@/components/HolidayOverview";
import { BillsStats } from "@/components/BillsStats";
import { DayHourlyCard } from "@/components/DayHourlyCard";
import { JatakPosts } from "@/components/JatakPosts";
import { JatakFeed } from "@/components/JatakFeed";
import { TradepartnerDeliveries } from "@/components/TradepartnerDeliveries";
import { StockgroupOverview } from "@/components/StockgroupOverview";
import { SlikBestilling } from "@/components/SlikBestilling";
import { TilbudsAvis } from "@/components/TilbudsAvis";
import { Analyse } from "@/components/SlikBasketInsights";
import { DangoodsView } from "@/components/DangoodsView";
import { NotesBoard } from "@/components/NotesBoard";
import { DeliveriesBoard } from "@/components/DeliveriesBoard";
import { Vagtplan } from "@/components/Vagtplan";
import { InsightsView } from "@/components/InsightsView";
import { SuppliersBoard } from "@/components/SuppliersBoard";
import { TabSettings } from "@/components/TabSettings";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { WasteLog } from "@/components/WasteLog";
import { DocumentsArchive } from "@/components/DocumentsArchive";
import { ContractBuilder } from "@/components/ContractBuilder";
import { MobileBottomNav } from "@/components/MobileNav";
import { AppSettingsButton } from "@/components/AppSettings";

const FAL = "#FB923C";
const RIN = "#38BDF8";
const GREEN = "#34D399";
const RED = "#F87171";

const ALL_TABS = [["overview", "Oversigt"], ["insights", "Indsigt"], ["products", "Produkter"], ["categories", "Kategorier"], ["payment", "Betaling"], ["hours", "Åbningstid"], ["holidays", "Ferie"], ["finance", "Økonomi"], ["bills", "Regninger"], ["jatak", "Ja tak"], ["jatakfeed", "Ja tak opslag"], ["notes", "Noter"], ["deliveries", "Vareleveringer"], ["vagtplan", "Vagtplan"], ["suppliers", "Leverandører"], ["waste", "Svind/spild"], ["documents", "Dokumentarkiv"], ["kontrakter", "Kontrakter"], ["dangoods", "Dangoods"], ["tradepartner", "Tradepartner"], ["stockgroup", "Stockgroup"], ["slik", "Slik bestilling"], ["avis", "Tilbudsavis"], ["analyse", "Analyse"]];
const TAB_LABEL = Object.fromEntries(ALL_TABS);

const TAB_GROUPS = [
  { label: "Salg", icon: BarChart3, ids: ["overview", "insights", "products", "categories", "payment", "hours", "analyse"] },
  { label: "Økonomi", icon: Wallet, ids: ["finance", "bills", "dangoods", "suppliers"] },
  { label: "Lager & levering", icon: Boxes, ids: ["deliveries", "tradepartner", "stockgroup", "slik", "waste", "vagtplan"] },
  { label: "Markedsføring", icon: Megaphone, ids: ["jatakfeed", "jatak", "avis"] },
  { label: "Andet", icon: FileText, ids: ["holidays", "notes", "documents", "kontrakter"] },
];

function normalizePrefs(prefs) {
  const ids = ALL_TABS.map((t) => t[0]);
  let bar = (prefs?.bar || []).filter((id) => ids.includes(id));
  let menu = (prefs?.menu || []).filter((id) => ids.includes(id) && !bar.includes(id));
  if (!prefs) { bar = ids.slice(); menu = []; }
  bar = bar.filter((id) => id !== "overview");
  menu = menu.filter((id) => id !== "overview");
  bar.unshift("overview");
  ids.forEach((id) => { if (!bar.includes(id) && !menu.includes(id)) bar.push(id); });
  return { bar, menu };
}

const RANGES = [
  { id: "today", label: "I dag", startBack: 0, endBack: 0, gran: "day" },
  { id: "yesterday", label: "I går", startBack: 1, endBack: 1, gran: "day" },
  { id: "week", label: "Denne uge", gran: "day" },
  { id: "7", label: "Sidste 7 dage", startBack: 6, endBack: 0, gran: "day" },
  { id: "30", label: "Sidste 30 dage", startBack: 29, endBack: 0, gran: "day" },
  { id: "90", label: "Sidste 90 dage", startBack: 89, endBack: 0, gran: "week" },
  { id: "365", label: "Sidste 12 måneder", startBack: 364, endBack: 0, gran: "month" },
];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const MONTHS_DA = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
function fmtPeriod(p) {
  if (!p || typeof p !== "string") return p;
  if (p.includes("-U")) return "U" + p.split("-U")[1];
  const parts = p.split("-");
  if (parts.length === 3) return `${parseInt(parts[2], 10)}/${parseInt(parts[1], 10)}`;
  if (parts.length === 2) return MONTHS_DA[parseInt(parts[1], 10) - 1] || p;
  return p;
}

/* ---------- small pieces ---------- */
function Delta({ pct, suffix = "vs. forrige periode" }) {
  if (pct == null) return null;
  const up = pct >= 0;
  return (
    <div className="flex items-center gap-1.5 text-sm">
      <span className="inline-flex items-center gap-1 font-semibold" style={{ color: up ? GREEN : RED }}>
        {up ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
        {Math.abs(pct)}%
      </span>
      <span className="text-slate-500">{suffix}</span>
    </div>
  );
}

function Tile({ title, children, className = "", testId }) {
  return (
    <div className={`rounded-2xl bg-white border border-[#e9edf1] p-5 shadow-sm hover:shadow-md transition-shadow ${className}`} data-testid={testId}>
      {title && <h3 className="text-[13px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-3">{title}</h3>}
      {children}
    </div>
  );
}

function MiniTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-3 py-2 text-xs">
      <p className="text-slate-500 mb-1">{fmtPeriod(label)}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-500">{p.name}</span>
          <span className="ml-auto font-medium text-slate-800">
            {p.dataKey.includes("orders") ? num(p.value) : dkk(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-4 text-xs text-slate-500 mb-1">
      <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: FAL }} /> Falster</span>
      <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: RIN }} /> Ringsted</span>
    </div>
  );
}

function Gauge({ value, label }) {
  const max = Math.max(400, Math.ceil((value * 1.5) / 50) * 50);
  const frac = Math.max(0, Math.min(value / max, 1));
  const cx = 110, cy = 110, r = 88;
  const pt = (t) => {
    const th = Math.PI + t * Math.PI;
    return [cx + r * Math.cos(th), cy + r * Math.sin(th)];
  };
  const [lx, ly] = pt(0), [rx, ry] = pt(1), [vx, vy] = pt(frac);
  const [nx, ny] = pt(frac);
  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 220 130" className="w-full max-w-[240px]">
        <path d={`M ${lx} ${ly} A ${r} ${r} 0 0 1 ${rx} ${ry}`} fill="none" stroke="#e2e8f0" strokeWidth="14" strokeLinecap="round" />
        <path d={`M ${lx} ${ly} A ${r} ${r} 0 ${frac > 0.5 ? 1 : 0} 1 ${vx} ${vy}`} fill="none" stroke={RIN} strokeWidth="14" strokeLinecap="round" />
        <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#64748B" strokeWidth="3" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="6" fill="#64748B" />
        <text x={lx} y="126" fill="#64748B" fontSize="11" textAnchor="middle">0</text>
        <text x={rx} y="126" fill="#64748B" fontSize="11" textAnchor="middle">{Math.round(max / 1000)}k</text>
      </svg>
      <div className="font-heading text-3xl font-bold text-slate-900 tabular-nums -mt-2">{dkk(value)}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );
}

/* ---------- main ---------- */
export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rangeId, setRangeId] = useState("today");
  const [customRange, setCustomRange] = useState({ from: undefined, to: undefined });
  const [openCal, setOpenCal] = useState(false);
  const [store, setStore] = useState("all");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [goals, setGoals] = useState([]);
  const [editing, setEditing] = useState(null);
  const [goalVal, setGoalVal] = useState("");
  const [tab, setTab] = useState(() => {
    const q = new URLSearchParams(window.location.search).get("tab");
    return q && TAB_LABEL[q] ? q : "overview";
  });
  const [dateParams, setDateParams] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [tabPrefs, setTabPrefs] = useState(() => normalizePrefs(null));
  const [settingsOpen, setSettingsOpen] = useState(false);

  const loadGoals = useCallback(async () => {
    try { const { data } = await api.get("/goals"); setGoals(data); } catch { /* */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    let start, end, gran;
    const today = new Date();
    if (rangeId === "custom" && customRange.from && customRange.to) {
      start = customRange.from; end = customRange.to;
      const span = Math.round((end - start) / 86400000) + 1;
      gran = span <= 31 ? "day" : span <= 92 ? "week" : "month";
    } else if (rangeId === "week") {
      const dow = (today.getDay() + 6) % 7; // 0 = mandag
      start = new Date(); start.setDate(today.getDate() - dow);
      end = new Date();
      gran = "day";
    } else {
      const r = RANGES.find((x) => x.id === rangeId) || RANGES[3];
      end = new Date(); end.setDate(end.getDate() - r.endBack);
      start = new Date(); start.setDate(start.getDate() - r.startBack);
      gran = r.gran;
    }
    const params = { from: iso(start), to: iso(end) };
    setDateParams({ from: params.from, to: params.to, gran });
    try {
      const [s, t, c, p] = await Promise.all([
        api.get("/kpi/summary", { params: { ...params, store } }),
        api.get("/kpi/trend", { params: { ...params, granularity: gran } }),
        api.get("/kpi/categories", { params: { ...params, store } }),
        api.get("/kpi/products", { params: { ...params, store } }),
      ]);
      setSummary(s.data); setTrend(t.data); setCategories(c.data); setProducts(p.data);
      setLastUpdated(new Date());
    } catch {
      toast.error("Kunne ikke hente data. Prøv igen.");
    } finally {
      setLoading(false);
    }
  }, [rangeId, store, customRange]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadGoals(); }, [loadGoals]);
  useEffect(() => { api.get("/preferences/tabs").then(({ data }) => setTabPrefs(normalizePrefs(data))).catch(() => {}); }, []);

  const lastSyncRef = useRef(0);
  const syncAndLoad = useCallback(() => {
    lastSyncRef.current = Date.now();
    api.post("/sync/live").catch(() => {}).finally(() => load());
  }, [load]);

  useEffect(() => { syncAndLoad(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => {
    const id = setInterval(syncAndLoad, 2 * 60 * 1000);
    return () => clearInterval(id);
  }, [syncAndLoad]);
  // Hent friske tal straks når fanen bliver synlig/fokuseret igen (browsere bremser timere i baggrundsfaner)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() - lastSyncRef.current > 30000) syncAndLoad();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => { document.removeEventListener("visibilitychange", onVisible); window.removeEventListener("focus", onVisible); };
  }, [syncAndLoad]);

  const saveGoal = async (s) => {
    const n = parseFloat(String(goalVal).replace(/\./g, "").replace(",", "."));
    if (!n || n <= 0) { toast.error("Indtast et gyldigt beløb"); return; }
    try { await api.put("/goals", { store: s, monthly_goal: n }); setEditing(null); await loadGoals(); toast.success("Månedsmål opdateret"); }
    catch { toast.error("Kunne ikke gemme"); }
  };

  const saveTabPrefs = async (bar, menu) => {
    const np = normalizePrefs({ bar, menu });
    setTabPrefs(np);
    try { await api.put("/preferences/tabs", np); toast.success("Faner opdateret"); }
    catch { toast.error("Kunne ikke gemme faner"); }
  };

  const t = summary?.totals;
  const margin = t && t.revenue ? Math.round((t.profit / t.revenue) * 100) : 0;
  const topCats = [...categories].sort((a, b) => (b.falster + b.ringsted) - (a.falster + a.ringsted)).slice(0, 6);
  const maxCat = Math.max(...topCats.map((c) => c.falster + c.ringsted), 1);
  const maxProd = Math.max(...products.map((p) => p.revenue), 1);
  const clock = new Date().toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" });

  const srcLabel = summary?.source === "live" ? "Live data"
    : summary?.source === "mixed" ? "Live + demo"
    : summary?.source === "syncing" ? "Henter live-data…" : "Demo-data";

  return (
    <div className="min-h-screen bg-[#F6F7F9] text-slate-800 flex">
      {/* Sidemenu (desktop) */}
      <aside className="hidden lg:flex lg:flex-col w-60 shrink-0 bg-white border-r border-[#e9edf1] h-screen sticky top-0">
        <div className="flex items-center gap-2.5 px-5 h-16 border-b border-[#eef2f6] shrink-0">
          <div className="w-9 h-9 rounded-lg bg-[#C8102E] flex items-center justify-center shadow-sm shadow-[#C8102E]/30">
            <Store className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div className="leading-tight">
            <p className="font-heading font-extrabold tracking-tight text-[15px] text-slate-900">Lagersalg</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Stats</p>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5" data-testid="sidebar-nav">
          {TAB_GROUPS.map((g) => (
            <div key={g.label}>
              <div className="flex items-center gap-1.5 px-2 mb-1.5">
                <g.icon className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">{g.label}</span>
              </div>
              <div className="space-y-0.5">
                {g.ids.map((id) => (
                  <button key={id} onClick={() => setTab(id)} data-testid={`tab-${id}`}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${tab === id ? "bg-[#C8102E]/10 text-[#C8102E]" : "text-slate-600 hover:bg-[#f4f6f8] hover:text-slate-900"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${tab === id ? "bg-[#C8102E]" : "bg-slate-300"}`} />
                    {TAB_LABEL[id]}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      {/* Indhold */}
      <div className="flex-1 min-w-0 flex flex-col">
      {/* Top bar */}
      <header className="bg-white border-b border-[#e9edf1] px-4 lg:px-6 py-3 sm:py-0 sm:h-16 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sticky top-0 z-30">
        <div className="flex items-center gap-3 min-w-0">
          <div className="lg:hidden w-8 h-8 rounded-lg bg-[#C8102E] flex items-center justify-center shrink-0">
            <Store className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          <h1 className="font-heading font-bold tracking-tight text-lg text-slate-900 truncate">{TAB_LABEL[tab]}</h1>
          {lastUpdated && (
            <span key={lastUpdated.getTime()} className="update-flash hidden md:inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 px-2.5 py-1 text-xs font-medium text-emerald-600 shrink-0">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              Opdateret kl. {lastUpdated.toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <Select value={store} onValueChange={setStore}>
            <SelectTrigger className="h-9 rounded-lg bg-[#ffffff] border-[#e2e8f0] text-slate-700 flex-1 min-w-[130px] sm:flex-none sm:w-[150px]" data-testid="store-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-lg bg-[#ffffff] border-[#e2e8f0] text-slate-700">
              <SelectItem value="all">Begge butikker</SelectItem>
              <SelectItem value="falster">Falster</SelectItem>
              <SelectItem value="ringsted">Ringsted</SelectItem>
            </SelectContent>
          </Select>
          <Select value={rangeId === "custom" ? "" : rangeId} onValueChange={setRangeId}>
            <SelectTrigger className="h-9 rounded-lg bg-[#ffffff] border-[#e2e8f0] text-slate-700 flex-1 min-w-[140px] sm:flex-none sm:w-[160px]" data-testid="range-filter-select">
              <SelectValue placeholder="Brugerdefineret" />
            </SelectTrigger>
            <SelectContent className="rounded-lg bg-[#ffffff] border-[#e2e8f0] text-slate-700">
              {RANGES.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Popover open={openCal} onOpenChange={setOpenCal}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-9 rounded-lg bg-[#ffffff] border-[#e2e8f0] text-slate-700 hover:bg-[#eef2f6] hover:text-slate-900 gap-2" data-testid="date-range-btn">
                <CalendarDays className="w-4 h-4" />
                {rangeId === "custom" && customRange.from && customRange.to ? `${iso(customRange.from)} → ${iso(customRange.to)}` : "Datoer"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white rounded-lg" align="end">
              <Calendar
                mode="range"
                selected={customRange}
                onSelect={(r) => {
                  setCustomRange(r || { from: undefined, to: undefined });
                  if (r?.from && r?.to) { setRangeId("custom"); setOpenCal(false); }
                }}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
          <Button variant="outline" size="icon" onClick={async () => { setLoading(true); try { await api.post("/sync/live"); } catch { /* */ } load(); loadGoals(); }} className="h-9 w-9 rounded-lg bg-[#ffffff] border-[#e2e8f0] text-slate-700 hover:bg-[#eef2f6] hover:text-slate-900" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button variant="outline" size="icon" onClick={() => { try { document.documentElement.requestFullscreen?.(); } catch { /* */ } navigate("/kiosk"); }} className="hidden sm:inline-flex h-9 w-9 rounded-lg bg-[#ffffff] border-[#e2e8f0] text-slate-700 hover:bg-[#eef2f6] hover:text-slate-900" data-testid="kiosk-btn" title="Kiosk-visning">
            <Monitor className="w-4 h-4" />
          </Button>
          <AppSettingsButton className="hidden lg:inline-flex" />
          <Button variant="ghost" size="sm" onClick={logout} className="hidden lg:inline-flex h-9 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-[#eef2f6]" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-1.5" /> Log ud
          </Button>
        </div>
      </header>

      <main className="p-4 lg:p-6 pb-28 lg:pb-6">
        {/* Bento grid */}
        {tab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 auto-rows-min">
          {/* Omsætning (tall + chart) */}
          <Tile title="Omsætning inkl. moms" className="xl:row-span-2" testId="kpi-revenue">
            <div className="font-heading text-4xl font-bold tabular-nums text-slate-900 mb-1">{dkk(t?.revenue)}</div>
            <Delta pct={summary?.change.revenue_pct} />
            <div className="mt-5">
              <Legend />
              <ResponsiveContainer width="100%" height={150}>
                <AreaChart data={trend} margin={{ top: 6, right: 4, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gf" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={FAL} stopOpacity={0.35} /><stop offset="100%" stopColor={FAL} stopOpacity={0} /></linearGradient>
                    <linearGradient id="gr" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={RIN} stopOpacity={0.35} /><stop offset="100%" stopColor={RIN} stopOpacity={0} /></linearGradient>
                  </defs>
                  <XAxis dataKey="period" tickFormatter={fmtPeriod} tick={{ fontSize: 10, fill: "#64748B" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} minTickGap={22} interval="preserveStartEnd" />
                  <Tooltip content={<MiniTooltip />} />
                  <Area type="monotone" dataKey="falster_revenue" name="Falster" stroke={FAL} strokeWidth={2} fill="url(#gf)" dot={false} />
                  <Area type="monotone" dataKey="ringsted_revenue" name="Ringsted" stroke={RIN} strokeWidth={2} fill="url(#gr)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Tile>

          {/* Antal salg (tall + chart) */}
          <Tile title="Antal salg" className="xl:row-span-2" testId="kpi-orders">
            <div className="font-heading text-4xl font-bold tabular-nums text-slate-900 mb-1">{num(t?.orders)}</div>
            <Delta pct={summary?.change.orders_pct} />
            <div className="mt-5">
              <Legend />
              <ResponsiveContainer width="100%" height={150}>
                <LineChart data={trend} margin={{ top: 6, right: 4, left: 0, bottom: 0 }}>
                  <XAxis dataKey="period" tickFormatter={fmtPeriod} tick={{ fontSize: 10, fill: "#64748B" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} minTickGap={22} interval="preserveStartEnd" />
                  <Tooltip content={<MiniTooltip />} />
                  <Line type="monotone" dataKey="falster_orders" name="Falster" stroke={FAL} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="ringsted_orders" name="Ringsted" stroke={RIN} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Tile>

          {/* Profit */}
          <Tile title="Profit" testId="kpi-profit">
            <div className="font-heading text-4xl font-bold tabular-nums text-slate-900 mb-1">{dkk(t?.profit)}</div>
            <Delta pct={summary?.change.profit_pct} />
            <div className="mt-4 pt-4 border-t border-[#e2e8f0]">
              <div className="font-heading text-3xl font-bold text-slate-900">{margin}<span className="text-lg text-slate-500">%</span></div>
              <div className="text-xs text-slate-500 mt-0.5">Profitmargin</div>
            </div>
          </Tile>

          {/* Nøgletal / P&L-like (tall) */}
          <Tile title="Nøgletal — perioden" className="xl:row-span-2" testId="kpi-keyfigures">
            <ul className="text-sm">
              {[
                ["Omsætning inkl. moms", dkk(t?.revenue)],
                ["Profit", dkk(t?.profit)],
                ["Total rabat", dkk(t?.discount)],
                ["__div", ""],
                ["Gns. kurv størrelse", dkk(t?.avg_basket)],
                ["Gns. varer pr. salg", dec(t?.avg_items)],
                ["Gns. profit pr. salg", dkk(t?.avg_profit)],
                ["Gns. retur pr. salg", dkk(t?.avg_return)],
              ].map(([k, v], i) =>
                k === "__div" ? (
                  <li key={i} className="my-2 border-t border-[#e2e8f0]" />
                ) : (
                  <li key={i} className="flex items-center justify-between py-2">
                    <span className="text-slate-500">{k}</span>
                    <span className="font-medium tabular-nums text-slate-800">{v}</span>
                  </li>
                )
              )}
            </ul>
          </Tile>

          {/* Gauge */}
          <Tile title="Gns. kurv størrelse" testId="kpi-avg-basket">
            <Gauge value={t?.avg_basket || 0} label="pr. salg" />
          </Tile>

          {/* Salg pr. butik */}
          <Tile title="Salg pr. butik" testId="store-comparison">
            {["falster", "ringsted"].map((s) => {
              const d = summary?.stores?.[s];
              const c = s === "falster" ? FAL : RIN;
              return (
                <div key={s} className="py-2.5 border-b border-[#e2e8f0] last:border-0" data-testid={`store-card-${s}`}>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-slate-700 capitalize">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />{s}
                    </span>
                    <span className="font-heading text-xl font-bold tabular-nums text-slate-900">{dkk(d?.revenue)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-500 mt-1 pl-4.5">
                    <span>{num(d?.orders)} salg</span>
                    <span>gns. {dkk(d?.aov)}</span>
                  </div>
                </div>
              );
            })}
          </Tile>

          {/* Topprodukter */}
          <Tile title="Topprodukter" testId="top-products">
            {products.length === 0 && (
              <p className="text-sm text-slate-500 py-6" data-testid="products-empty">Ingen synkroniserede varedata for perioden endnu.</p>
            )}
            <ul className="space-y-2.5">
              {products.slice(0, 6).map((p, i) => (
                <li key={`${p.product}-${i}`} data-testid={`top-product-${i}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-slate-500 truncate pr-2">{p.product}</span>
                    <span className="font-medium tabular-nums text-slate-800 whitespace-nowrap">{dkk(p.revenue)}</span>
                  </div>
                  <div className="h-1 rounded bg-[#e2e8f0] overflow-hidden">
                    <div className="h-full rounded" style={{ width: `${(p.revenue / maxProd) * 100}%`, background: RIN }} />
                  </div>
                </li>
              ))}
            </ul>
          </Tile>

          {/* Salg pr. kategori */}
          <Tile title="Salg pr. kategori" testId="category-chart">
            {topCats.length === 0 && (
              <p className="text-sm text-slate-500 py-6" data-testid="categories-empty">Ingen synkroniserede varedata for perioden endnu.</p>
            )}
            <ul className="space-y-2.5">
              {topCats.map((c) => {
                const total = c.falster + c.ringsted;
                return (
                  <li key={c.category}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-slate-500">{c.category}</span>
                      <span className="font-medium tabular-nums text-slate-800">{dkk(total)}</span>
                    </div>
                    <div className="h-1.5 rounded bg-[#e2e8f0] overflow-hidden flex">
                      <div className="h-full" style={{ width: `${(c.falster / maxCat) * 100}%`, background: FAL }} />
                      <div className="h-full" style={{ width: `${(c.ringsted / maxCat) * 100}%`, background: RIN }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </Tile>

          {/* Månedens mål */}
          <Tile title="Månedens mål" className="md:col-span-2" testId="goals-section">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {goals.map((g) => {
                const pct = Math.max(0, Math.min(g.pct ?? 0, 100));
                const reached = (g.pct ?? 0) >= 100;
                const c = g.store === "falster" ? FAL : RIN;
                return (
                  <div key={g.store} data-testid={`goal-${g.store}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="flex items-center gap-2 text-slate-700 font-medium">
                        <Target className="w-3.5 h-3.5" style={{ color: c }} /> {g.name}
                      </span>
                      {editing === g.store ? (
                        <div className="flex items-center gap-1">
                          <Input value={goalVal} onChange={(e) => setGoalVal(e.target.value)} className="h-7 w-24 rounded-md bg-[#f8fafc] border-[#e2e8f0] text-slate-800 text-xs" data-testid={`goal-input-${g.store}`} />
                          <Button size="icon" className="h-7 w-7 rounded-md bg-[#38BDF8] hover:bg-sky-400 text-[#0b1120]" onClick={() => saveGoal(g.store)} data-testid={`goal-save-${g.store}`}><Check className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-md text-slate-500" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button>
                        </div>
                      ) : (
                        <button className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1" onClick={() => { setEditing(g.store); setGoalVal(String(Math.round(g.monthly_goal))); }} data-testid={`goal-edit-${g.store}`}>
                          <Pencil className="w-3 h-3" /> mål
                        </button>
                      )}
                    </div>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className="font-heading text-2xl font-bold tabular-nums text-slate-900">{dkk(g.mtd_revenue)}</span>
                      <span className="text-xs text-slate-500">af {dkk(g.monthly_goal)}</span>
                    </div>
                    <div className="h-2 rounded bg-[#e2e8f0] overflow-hidden">
                      <div className="h-full rounded transition-all duration-700" style={{ width: `${pct}%`, background: reached ? GREEN : c }} />
                    </div>
                    <div className="mt-1.5 text-xs font-medium" style={{ color: reached ? GREEN : c }}>
                      {g.pct ?? 0}% af målet{reached ? " — nået!" : ""}
                    </div>
                  </div>
                );
              })}
            </div>
          </Tile>

          {/* Salg pr. time — valgt dag */}
          <DayHourlyCard store={store} />
        </div>
        )}

        {tab === "insights" && (
          <InsightsView dateParams={dateParams} />
        )}

        {tab === "products" && (
          <ProductStats store={store} dateParams={dateParams} />
        )}

        {tab === "categories" && (
          <CategoryStats store={store} dateParams={dateParams} />
        )}

        {tab === "payment" && (
          <PaymentMethods store={store} dateParams={dateParams} />
        )}

        {tab === "hours" && (
          <HourlyStats store={store} />
        )}

        {tab === "holidays" && (
          <HolidayOverview />
        )}

        {tab === "finance" && (
          <FinanceStats store={store} />
        )}

        {tab === "bills" && (
          <BillsStats />
        )}

        {tab === "jatak" && (
          <JatakPosts />
        )}

        {tab === "jatakfeed" && (
          <JatakFeed />
        )}

        {tab === "dangoods" && (
          <DangoodsView store={store} admin />
        )}

        {tab === "tradepartner" && (
          <TradepartnerDeliveries />
        )}

        {tab === "stockgroup" && (
          <StockgroupOverview />
        )}

        {tab === "slik" && (
          <SlikBestilling />
        )}

        {tab === "avis" && (
          <TilbudsAvis />
        )}

        {tab === "analyse" && (
          <Analyse />
        )}

        {tab === "notes" && (
          <NotesBoard />
        )}

        {tab === "deliveries" && (
          <DeliveriesBoard />
        )}

        {tab === "vagtplan" && (
          <Vagtplan />
        )}

        {tab === "suppliers" && (
          <SuppliersBoard />
        )}

        {tab === "waste" && (
          <WasteLog />
        )}

        {tab === "documents" && (
          <DocumentsArchive />
        )}

        {tab === "kontrakter" && (
          <ContractBuilder />
        )}

        {/* Footer */}
        <div className="mt-6 flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <span className="font-heading font-semibold text-slate-500">Customers First — Salgsoverblik</span>
            <span>·</span>
            {summary && <span>{summary.range.from} → {summary.range.to}</span>}
            <span>·</span>
            <span style={{ color: summary?.source === "live" ? GREEN : summary?.source === "syncing" ? "#FBBF24" : "#64748B" }}>{srcLabel}</span>
          </div>
          <span className="tabular-nums text-slate-500">{clock}</span>
        </div>
      </main>
      </div>
      <MobileBottomNav tab={tab} setTab={setTab} groups={TAB_GROUPS} labels={TAB_LABEL} onLogout={logout} />
    </div>
  );
}
